import React, { useState } from 'react';
import { ShoppingBagIcon, MailIcon, StoreIcon, TagIcon, PackageIcon, CalendarIcon, BriefcaseIcon } from 'lucide-react';
import { ConnectionPageLayout } from '../../components/connections/ConnectionPageLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { OpportunityItem } from '../../components/connections/OpportunityPreviewCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const ConnectWithVendors = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    projectDescription: '',
    timeline: '',
    budget: '',
    productTypes: [] as string[],
    quantityNeeded: '',
    customizationNeeded: false,
    shippingRequirements: '',
    termsAgreed: false
  });
  const [submitted, setSubmitted] = useState(false);
  const inputClass = `w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`;
  const labelClass = `block text-sm font-medium mb-2 ${dk.textSecondary}`;
  const sectionHeadingClass = `text-xl font-semibold mb-6 flex items-center pb-2 border-b ${dk.textPrimary} ${dk.border}`;
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleProductTypeChange = (productType: string) => {
    setFormData(prev => ({
      ...prev,
      productTypes: prev.productTypes.includes(productType) ? prev.productTypes.filter(p => p !== productType) : [...prev.productTypes, productType]
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const productTypes = ['Clothing', 'Accessories', 'Art Prints', 'Bookmarks', 'Jewelry', 'Home Decor', 'Stationery', 'Reading Accessories', 'Mugs/Drinkware', 'Tote Bags', 'Phone Cases', 'Candles'];
  // Filters
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'relevance',
      label: 'Relevance'
    }, {
      value: 'name-asc',
      label: 'Name (A-Z)'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }],
    value: 'relevance',
    onChange: () => {}
  }, {
    id: 'availability',
    title: 'Availability',
    type: 'checkbox',
    options: [{
      value: 'wholesale',
      label: 'Wholesale Open'
    }, {
      value: 'orders',
      label: 'Taking Orders'
    }],
    value: [],
    onChange: () => {}
  }, {
    id: 'productType',
    title: 'Product Type',
    type: 'checkbox',
    options: productTypes.map(p => ({
      value: p,
      label: p
    })),
    value: [],
    onChange: () => {}
  }];
  // Opportunities
  const opportunities: OpportunityItem[] = [{
    id: '1',
    title: 'Fantasy Series Merchandise Collection',
    poster: 'Epic Realms Publishing',
    deadline: '2023-12-15',
    type: 'Revenue Share',
    description: 'Seeking merchandise partner to create a premium collection for our bestselling fantasy series.'
  }, {
    id: '2',
    title: 'Romance Author Branded Merchandise',
    poster: 'Heart & Soul Books',
    deadline: '2023-11-30',
    type: 'Wholesale',
    description: 'Bestselling romance author looking for merchandise partner to create branded products.'
  }, {
    id: '3',
    title: 'Mystery Series Bookish Accessories',
    poster: 'Clue Press',
    deadline: '2023-12-30',
    type: 'White Label',
    description: 'Publisher seeking partner to create themed bookmarks and reading lights.'
  }];
  // Form Content
  const formContent = <form onSubmit={handleSubmit} className="space-y-10" data-id="element-18365" data-mp="160g4pyzs0xcm">
      {/* Contact Information */}
      <div data-id="element-18366" data-mp="1as7yeie8bj6z">
        <h2 className={sectionHeadingClass} data-id="element-18367" data-mp="1ary5alxw0vx2">
          <MailIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-18368" data-mp="1atssy80jn24u" />
          Contact Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-18369" data-mp="wygmx4gxtp8m">
          <div data-id="element-18370" data-mp="12061uv7a6n1z">
            <label htmlFor="name" className={labelClass} data-id="element-18371" data-mp="11zmgf4if9gj7">
              Your Name *
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className={inputClass} placeholder="John Doe" required data-id="element-18372" data-mp="n46vboztkuyy" />
          </div>
          <div data-id="element-18373" data-mp="4fwdhma9lbaz">
            <label htmlFor="email" className={labelClass} data-id="element-18374" data-mp="4g61537u76di">
              Email Address *
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} placeholder="john@example.com" required data-id="element-18375" data-mp="dehv9f7wwmap" />
          </div>
          <div className="md:col-span-2" data-id="element-18376" data-mp="114lvrdpt05xh">
            <label htmlFor="company" className={labelClass} data-id="element-18377" data-mp="113klhbgm3ngt">
              Company/Publisher
            </label>
            <div className="relative" data-id="element-18378" data-mp="w72hwex0pv62">
              <BriefcaseIcon className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-18379" data-mp="1b1qjhhzas9ge" />
              <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} className={`${inputClass} pl-12`} placeholder="Company Name (Optional)" data-id="element-18380" data-mp="164qcn4umj2o8" />
            </div>
          </div>
        </div>
      </div>

      {/* Project Details */}
      <div data-id="element-18381" data-mp="1rwudheldnjo0">
        <h2 className={sectionHeadingClass} data-id="element-18382" data-mp="1rwkre23gdwnl">
          <StoreIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-18383" data-mp="1wuvyi52d5t24" />
          Project Details
        </h2>
        <div className="space-y-6" data-id="element-18384" data-mp="20v6g6eziq7wt">
          <div data-id="element-18385" data-mp="20u4r36lo3kw3">
            <label htmlFor="projectType" className={labelClass} data-id="element-18386" data-mp="20y0vxt7l74jw">
              Project Type *
            </label>
            <select id="projectType" name="projectType" value={formData.projectType} onChange={handleInputChange} className={inputClass} required data-id="element-18387" data-mp="2au3yh06cp57d">
              <option value="" data-id="element-18388" data-mp="ggw0izn2wdhi">Select a project type</option>
              <option value="Book Launch Merchandise" data-id="element-18389" data-mp="8wbo58sybb2e">
                Book Launch Merchandise
              </option>
              <option value="Author Brand Merchandise" data-id="element-18390" data-mp="45lidugm5676">
                Author Brand Merchandise
              </option>
              <option value="Series Merchandise" data-id="element-18391" data-mp="3vcy0qct8fpoo">Series Merchandise</option>
              <option value="Convention/Event Merchandise" data-id="element-18392" data-mp="3qdld3pujeel1">
                Convention/Event Merchandise
              </option>
              <option value="Bookstore Merchandise" data-id="element-18393" data-mp="ru4ny28a34vk">
                Bookstore Merchandise
              </option>
              <option value="Book Club Merchandise" data-id="element-18394" data-mp="mx4jfvr2ua6k">
                Book Club Merchandise
              </option>
              <option value="Other" data-id="element-18395" data-mp="hzcqqzilyk1b">Other</option>
            </select>
          </div>
          <div data-id="element-18396" data-mp="cxx9lijjwxo7">
            <label htmlFor="projectDescription" className={labelClass} data-id="element-18397" data-mp="d0je19b5ax27">
              Project Description *
            </label>
            <textarea id="projectDescription" name="projectDescription" value={formData.projectDescription} onChange={handleInputChange} rows={5} className={inputClass} placeholder="Please describe your merchandise project in detail..." required data-id="element-18398" data-mp="2xnwdngxh6mlj" />
          </div>
        </div>
      </div>

      {/* Product Types */}
      <div data-id="element-18399" data-mp="3idyvtuhj4vrr">
        <h2 className={sectionHeadingClass} data-id="element-18400" data-mp="3idqi60dl1mfi">
          <TagIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-18401" data-mp="9x56gbtkgf8p" />
          Product Types
        </h2>
        <div data-id="element-18402" data-mp="ex9my1uh59yx">
          <label className={labelClass} data-id="element-18403" data-mp="evos0b48zp0j">
            Select product types you're interested in *
          </label>
          <div className="flex flex-wrap gap-2" data-id="element-18404" data-mp="14jxtojnfoxl">
            {productTypes.map(productType => <button type="button" key={productType} onClick={() => handleProductTypeChange(productType)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${formData.productTypes.includes(productType) ? `${rt.bgDark} text-white shadow-md border-transparent` : `${dk.badgeNeutral} ${dk.border}`}`} data-id="element-18405" data-mp="55douqse1jup">
                {productType}
              </button>)}
          </div>
          {formData.productTypes.length === 0 && <p className="mt-2 text-sm text-red-500 flex items-center" data-id="element-18406" data-mp="2n4d55zrw1nzg">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-2" data-id="element-18407" data-mp="2n550d9hkpjp7"></span>
              Please select at least one product type
            </p>}
        </div>
      </div>

      {/* Quantity and Timeline */}
      <div data-id="element-18408" data-mp="29cove3543bln">
        <h2 className={sectionHeadingClass} data-id="element-18409" data-mp="29dhuchxtwwt8">
          <PackageIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-18410" data-mp="29e9p5qjlqqig" />
          Quantity and Timeline
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-18411" data-mp="24fyh9lcvaq2v">
          <div data-id="element-18412" data-mp="3kqsn12o88nnj">
            <label htmlFor="quantityNeeded" className={labelClass} data-id="element-18413" data-mp="3kqailirev34j">
              Estimated Quantity Needed *
            </label>
            <input type="text" id="quantityNeeded" name="quantityNeeded" value={formData.quantityNeeded} onChange={handleInputChange} className={inputClass} placeholder="e.g., 100-500 units" required data-id="element-18414" data-mp="oq9mc8k0jg6f" />
          </div>
          <div data-id="element-18415" data-mp="a0bv1jqsead0">
            <label htmlFor="timeline" className={labelClass} data-id="element-18416" data-mp="a0txwz7yfmtw">
              Project Timeline *
            </label>
            <select id="timeline" name="timeline" value={formData.timeline} onChange={handleInputChange} className={inputClass} required data-id="element-18417" data-mp="53lbgmfrwwmu">
              <option value="" data-id="element-18418" data-mp="3n855naeibkiq">Select timeline</option>
              <option value="Less than 1 month" data-id="element-18419" data-mp="3n9pz0wp34xf0">Less than 1 month</option>
              <option value="1-2 months" data-id="element-18420" data-mp="1qek54euder7r">1-2 months</option>
              <option value="2-3 months" data-id="element-18421" data-mp="1lgik3oddwhtn">2-3 months</option>
              <option value="3+ months" data-id="element-18422" data-mp="1lh1vsncfy9z8">3+ months</option>
              <option value="Flexible" data-id="element-18423" data-mp="1t0kv645zsftz">Flexible</option>
            </select>
          </div>
        </div>
      </div>

      {/* Additional Details */}
      <div data-id="element-18424" data-mp="1xxbbmslpldfx">
        <h2 className={sectionHeadingClass} data-id="element-18425" data-mp="1xwrq71wuo6x5">
          <CalendarIcon className={`h-5 w-5 mr-2 ${rt.textPrimary}`} data-id="element-18426" data-mp="22pkxnxjpdtcx" />
          Additional Details
        </h2>
        <div className="space-y-6" data-id="element-18427" data-mp="27nmd88hx7ajn">
          <div className={`p-4 rounded-lg border ${dk.cardElevated}`} data-id="element-18428" data-mp="27r0j3qpsm1vu">
            <div className="flex items-start" data-id="element-18429" data-mp="1a2qbmw78mlxc">
              <div className="flex items-center h-5 mt-1" data-id="element-18430" data-mp="1f00at3kfibyd">
                <input id="customizationNeeded" name="customizationNeeded" type="checkbox" checked={formData.customizationNeeded} onChange={handleCheckboxChange} className={`h-4 w-4 border-gray-300 rounded cursor-pointer ${rt.checkbox}`} data-id="element-18431" data-mp="1f0igeqsze2iy" />
              </div>
              <div className="ml-3" data-id="element-18432" data-mp="30ca18bu20e0w">
                <label htmlFor="customizationNeeded" className={`text-sm font-medium ${dk.textHeading}`} data-id="element-18433" data-mp="30ffve5hji63h">
                  Custom Design Services Needed
                </label>
                <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-18434" data-mp="19cjsx4z64i2m">
                  Check if you need design assistance for your merchandise
                </p>
              </div>
            </div>
          </div>
          <div data-id="element-18435" data-mp="hk2omnm3balp">
            <label htmlFor="shippingRequirements" className={labelClass} data-id="element-18436" data-mp="hlnisc4hsrj1">
              Shipping/Distribution Requirements
            </label>
            <textarea id="shippingRequirements" name="shippingRequirements" value={formData.shippingRequirements} onChange={handleInputChange} rows={3} className={inputClass} placeholder="Describe any specific shipping or distribution needs..." data-id="element-18437" data-mp="cjy66s1mg5su" />
          </div>
          <div data-id="element-18438" data-mp="3q0fa7ikz3fzd">
            <label htmlFor="budget" className={labelClass} data-id="element-18439" data-mp="3pyuqyp43zjgt">
              Budget Range
            </label>
            <select id="budget" name="budget" value={formData.budget} onChange={handleInputChange} className={inputClass} data-id="element-18440" data-mp="3fuw84ul48klz">
              <option value="" data-id="element-18441" data-mp="3axnn8m8l22h1">Select budget range</option>
              <option value="Under $500" data-id="element-18442" data-mp="3ay5nrv2a32ur">Under $500</option>
              <option value="$500 - $1,000" data-id="element-18443" data-mp="2hb03mu1xp19">$500 - $1,000</option>
              <option value="$1,000 - $2,500" data-id="element-18444" data-mp="2fzvjkss8d93">$1,000 - $2,500</option>
              <option value="$2,500 - $5,000" data-id="element-18445" data-mp="7gf6k5pld8nw">$2,500 - $5,000</option>
              <option value="$5,000 - $10,000" data-id="element-18446" data-mp="3opu09zgfvtc9">$5,000 - $10,000</option>
              <option value="$10,000+" data-id="element-18447" data-mp="3to5dmk5y0lz8">$10,000+</option>
              <option value="Flexible" data-id="element-18448" data-mp="3tmkgqnw5oqy7">Flexible / To be discussed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Terms and Submit */}
      <div className={`pt-6 border-t ${dk.border}`} data-id="element-18449" data-mp="2i98jivuwj6l0">
        <div className="flex items-start mb-8" data-id="element-18450" data-mp="2i6vszub2vnwc">
          <div className="flex items-center h-5 mt-0.5" data-id="element-18451" data-mp="2ygd0jwm1qeur">
            <input id="termsAgreed" name="termsAgreed" type="checkbox" checked={formData.termsAgreed} onChange={handleCheckboxChange} className={`h-4 w-4 border-gray-300 rounded cursor-pointer ${rt.checkbox}`} required data-id="element-18452" data-mp="2yhxzrzjb2lyy" />
          </div>
          <div className="ml-3 text-sm" data-id="element-18453" data-mp="3hdxrd98dfni0">
            <label htmlFor="termsAgreed" className={dk.textSecondary} data-id="element-18454" data-mp="3hcwecz7soqxn">
              I agree to the{' '}
              <a href="#" className={`font-medium hover:underline ${rt.accentLink}`} data-id="element-18455" data-mp="3ce1o5c7i003i">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className={`font-medium hover:underline ${rt.accentLink}`} data-id="element-18456" data-mp="1w1mma0u6ebiv">
                Privacy Policy
              </a>
            </label>
          </div>
        </div>
        <button type="submit" className={`w-full py-3.5 px-6 rounded-lg font-bold text-lg shadow-lg transform transition-all hover:-translate-y-0.5 text-white ${rt.primaryButton}`} data-id="element-18457" data-mp="6laaowpd79t1">
          Submit Request
        </button>
      </div>
    </form>;
  return <ConnectionPageLayout title="Connect with Vendors" subtitle="Submit your merchandise project details to find the perfect vendor partner" icon={<ShoppingBagIcon className="h-8 w-8" data-id="element-18459" data-mp="qakna1ttdemx" />} vendorType="vendors" filterSections={filterSections} opportunities={opportunities} opportunitiesLink="/vendors/opportunities" formContent={formContent} formCTALabel="Submit a Merchandise Request" formCTADescription="Can't find the right vendor? Submit your project details and we'll help you find a match." data-id="element-18458" data-mp="l9x0xtgj5lz1" />;
};
export default ConnectWithVendors;